/**
 * Scope-compliance observer (client side).
 *
 * Phase 1 of the split-canonical sync refactor. Counterpart to
 * `party/server/shared/scope-observer.ts`. Observe-only; no behavior change.
 *
 * Output channels mirror the server side:
 *   - the counter SINK — by default `console.log` of a structured envelope
 *     `{ telemetry: "scope-observer", counter, ... }`, cheap and visible in
 *     browser devtools / browser-side log aggregation. A platform with no
 *     reader for it (native: a `console.log` per card write is a JSI hop into
 *     logcat, and nothing aggregates it) routes or silences it with
 *     `setScopeCounterSink`.
 *   - `Sentry.addBreadcrumb` — sampled at 10% on success / 100% on
 *     violation. Tied to the next captured Sentry event.
 *   - `Sentry.captureMessage` — never from this module in Phase 1; the
 *     server is the authoritative violation signal.
 *
 * Card content is never logged — only field-name sets and tag identifiers.
 */

import { CARD_FIELD_SCOPES } from '@flashka/shared/sync-engine/schema/field-scopes';
import type { SyncCardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import * as Sentry from '@sentry/react';

const BREADCRUMB_SAMPLE_RATE_OK = 0.1;

type PartitionLabel = 'shared-only' | 'personal-only' | 'mixed';

type CounterName = `scope.partition.${PartitionLabel}`;

type ObservationCtx = {
  source: string;
  deckId?: string;
  isLinked?: boolean;
};

function debugCohort(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('flashka_debug_sync') === 'true';
  } catch {
    return false;
  }
}

/** The structured counter envelope a sink receives — one per observed write. */
export type ScopeCounterEnvelope = ObservationCtx & {
  telemetry: 'scope-observer';
  side: 'client';
  counter: CounterName;
};

export type ScopeCounterSink = (envelope: ScopeCounterEnvelope) => void;

const defaultCounterSink: ScopeCounterSink = (envelope) => {
  console.log(JSON.stringify(envelope));
};

let counterSink: ScopeCounterSink | null = defaultCounterSink;

/**
 * Route the per-write partition counter somewhere else, or silence it with
 * `null`. Web keeps the default (`console.log`, read by browser-side log
 * aggregation); native passes `null` at bootstrap. The Sentry breadcrumb is
 * unaffected — it is sampled, and it travels with the next captured event.
 */
export function setScopeCounterSink(sink: ScopeCounterSink | null): void {
  counterSink = sink;
}

function emitCounter(name: CounterName, ctx: ObservationCtx): void {
  counterSink?.({ telemetry: 'scope-observer', side: 'client', counter: name, ...ctx });
}

function emitBreadcrumb(data: Record<string, unknown>, isViolation: boolean): void {
  if (!isViolation && !debugCohort() && Math.random() > BREADCRUMB_SAMPLE_RATE_OK) return;
  Sentry.addBreadcrumb({
    category: 'sync.scope-partition',
    level: isViolation ? 'warning' : 'info',
    data,
  });
}

function classifyPartial(partial: Partial<SyncCardProps>): {
  partition: PartitionLabel | 'empty';
  sharedKeys: string[];
  personalKeys: string[];
} {
  const sharedKeys: string[] = [];
  const personalKeys: string[] = [];
  for (const key of Object.keys(partial)) {
    const scope = CARD_FIELD_SCOPES[key as keyof typeof CARD_FIELD_SCOPES];
    if (scope === 'shared') sharedKeys.push(key);
    else if (scope === 'personal') personalKeys.push(key);
  }
  let partition: PartitionLabel | 'empty' = 'empty';
  if (sharedKeys.length > 0 && personalKeys.length > 0) partition = 'mixed';
  else if (sharedKeys.length > 0) partition = 'shared-only';
  else if (personalKeys.length > 0) partition = 'personal-only';
  return { partition, sharedKeys, personalKeys };
}

/**
 * Observe a partial card update before it is dispatched to the store.
 * Fires partition counter exactly once per call (not per field).
 *
 * Phase 1: observe only. The actual scope-routed write lands in Phase 5.
 */
export function observeClientCardPartition(
  partial: Partial<SyncCardProps>,
  ctx: ObservationCtx,
): void {
  const { partition, sharedKeys, personalKeys } = classifyPartial(partial);
  if (partition === 'empty') return;
  emitCounter(`scope.partition.${partition}`, ctx);
  emitBreadcrumb(
    {
      side: 'client',
      partition,
      sharedKeyCount: sharedKeys.length,
      personalKeyCount: personalKeys.length,
      ...ctx,
    },
    false,
  );
}

// Exported for unit tests.
export const _testOnly = { classifyPartial };
